import { useState, useEffect, createContext, useContext } from 'react';
import authApiService from '../services/authApi';

// Create Auth Context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      setLoading(true);
      // Check if we have tokens in localStorage
      const accessToken = localStorage.getItem('accessToken');
      const idToken = localStorage.getItem('idToken');
      const username = localStorage.getItem('username');

      if (accessToken && idToken) {
        // Verify token is still valid
        try {
          const result = await authApiService.verifyToken(accessToken);
          setUser({
            username: result.user.username,
            attributes: result.user.attributes,
            tokens: {
              accessToken,
              idToken
            }
          });
          setError(null);
        } catch (err) {
          // Token invalid, try to refresh
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken && username) {
            try {
              const refreshResult = await authApiService.refreshToken(refreshToken, username);
              localStorage.setItem('accessToken', refreshResult.tokens.accessToken);
              localStorage.setItem('idToken', refreshResult.tokens.idToken);
              const verifyResult = await authApiService.verifyToken(refreshResult.tokens.accessToken);
              setUser({
                username: verifyResult.user.username,
                attributes: verifyResult.user.attributes,
                tokens: {
                  accessToken: refreshResult.tokens.accessToken,
                  idToken: refreshResult.tokens.idToken
                }
              });
            } catch (refreshErr) {
              // Refresh failed, clear tokens
              localStorage.removeItem('accessToken');
              localStorage.removeItem('idToken');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('username');
              setUser(null);
            }
          } else {
            // No refresh token, clear everything
            localStorage.removeItem('accessToken');
            localStorage.removeItem('idToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('username');
            setUser(null);
          }
        }
      } else {
        setUser(null);
      }
      setError(null);
    } catch (err) {
      setUser(null);
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (username, password) => {
    try {
      setLoading(true);
      setError(null);
      const result = await authApiService.signIn(username, password);
      
      // Store tokens
      localStorage.setItem('accessToken', result.tokens.accessToken);
      localStorage.setItem('idToken', result.tokens.idToken);
      localStorage.setItem('refreshToken', result.tokens.refreshToken);
      
      // Use the username returned from the server (which is the actual Cognito username)
      const actualUsername = result.username || username;
      localStorage.setItem('username', actualUsername);

      // Get user info
      try {
        const userInfo = await authApiService.verifyToken(result.tokens.accessToken);
        const user = {
          username: userInfo.user.username || actualUsername,
          attributes: userInfo.user.attributes,
          tokens: result.tokens
        };
        setUser(user);
        return user;
      } catch (verifyError) {
        // If verification fails, still set user with what we have
        console.warn('Token verification failed, using basic user info:', verifyError);
        const user = {
          username: actualUsername,
          attributes: {},
          tokens: result.tokens
        };
        setUser(user);
        return user;
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (
    username,
    password,
    email,
    firstName,
    lastName,
    address,
    deviceId,
    deviceCode,
    storeName,
    storeAddress,
    storeCity,
    storeCountry,
    storeState,
    storeZip,
    storePhone
  ) => {
    try {
      setLoading(true);
      setError(null);
      const result = await authApiService.signUp(
        username,
        password,
        email,
        firstName,
        lastName,
        address,
        deviceId,
        deviceCode,
        storeName,
        storeAddress,
        storeCity,
        storeCountry,
        storeState,
        storeZip,
        storePhone
      );
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const confirmSignUp = async (username, code) => {
    try {
      setLoading(true);
      setError(null);
      await authApiService.confirmSignUp(username, code);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      // Clear tokens from localStorage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('username');
      setUser(null);
      setError(null);
    } catch (err) {
      setError(err.message);
      // Still clear tokens even if there's an error
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('username');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const getAccessToken = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        return null;
      }
      
      // Verify token is still valid
      try {
        await authApiService.verifyToken(accessToken);
        return accessToken;
      } catch (err) {
        // Token invalid, try to refresh
        const refreshToken = localStorage.getItem('refreshToken');
        const username = localStorage.getItem('username');
        
        if (refreshToken && username) {
          const result = await authApiService.refreshToken(refreshToken, username);
          localStorage.setItem('accessToken', result.tokens.accessToken);
          localStorage.setItem('idToken', result.tokens.idToken);
          return result.tokens.accessToken;
        }
        
        return null;
      }
    } catch (err) {
      console.error('Error getting access token:', err);
      return null;
    }
  };

  const forgotPassword = async (username) => {
    try {
      setLoading(true);
      setError(null);
      await authApiService.forgotPassword(username);
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const confirmPassword = async (username, code, newPassword) => {
    try {
      setLoading(true);
      setError(null);
      await authApiService.resetPassword(username, code, newPassword);
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      setLoading(true);
      setError(null);
      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error('Not authenticated. Please sign in again.');
      }
      await authApiService.changePassword(accessToken, currentPassword, newPassword);
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const resendSignUpCode = async (username) => {
    try {
      setLoading(true);
      setError(null);
      await authApiService.resendSignUpCode(username);
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    error,
    signIn,
    signUp,
    confirmSignUp,
    signOut,
    getAccessToken,
    checkAuthState,
    forgotPassword,
    confirmPassword,
    changePassword,
    resendSignUpCode
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
